// Management P&L → Excel (.xlsx) — the Executive-tab "P&L" download.
//
// One wide sheet mirroring the on-screen P&L: a column per operating-year month
// (March–Feb) plus Year-to-date, grouped ReportCategory → ReportSubCategory,
// with an operating result, an Exceptional Items block and a grand Total.
//
// Amounts are stored on the display sign the page uses (income positive,
// expenses negative). Income/expense rows show a positive magnitude (management-
// accounts style); the operating result and grand total keep their sign. YTD,
// category subtotals and the result/total rows are live formulas, so the sheet
// recalculates. exceljs is dynamically imported on use.
import type ExcelJS from 'exceljs'

interface PnlLine { values: number[]; ytd: number }
interface PnlSub extends PnlLine { sub: string }
interface PnlCategory { category: string; total: PnlLine; subs: PnlSub[] }
interface PnlMonth { period_no: number; name: string; short: string; cal_year: number; locked: boolean }
export interface PnlXlsxData {
  season: string
  entity_label: string
  months: PnlMonth[]
  last_refresh: string | null
  categories: PnlCategory[]
  operating: PnlLine
  exceptional: { subs: PnlSub[]; total: PnlLine }
  total: PnlLine
}

const GREEN = 'FF0B3D2E'
const SUBTOTAL_BG = 'FFEDE7D8'
const RESULT_BG = 'FFF0F4F1'
const BORDER = 'FFD8D8D8'
const MAG = '£#,##0;£#,##0;"–"'      // magnitude — negatives shown without sign
const SIGNED = '£#,##0;-£#,##0;"–"'  // result/total rows keep the sign

function colLetter(n: number): string {
  let s = ''
  while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26) }
  return s
}

export async function buildPnlWorkbook(data: PnlXlsxData): Promise<ExcelJS.Workbook> {
  const ExcelJSlib = (await import('exceljs')).default
  const wb = new ExcelJSlib.Workbook()
  wb.creator = 'NYMR Reports'
  wb.created = new Date()
  const ws = wb.addWorksheet('Management P&L', { views: [{ state: 'frozen', xSplit: 1, ySplit: 2 }] })

  const nMonths = data.months.length
  const firstMonthCol = 2
  const ytdCol = firstMonthCol + nMonths
  ws.getColumn(1).width = 40
  for (let c = firstMonthCol; c <= ytdCol; c++) ws.getColumn(c).width = 12

  const thin = { style: 'thin' as const, color: { argb: BORDER } }
  const box = (cell: ExcelJS.Cell) => { cell.border = { top: thin, left: thin, bottom: thin, right: thin } }
  const fillCell = (cell: ExcelJS.Cell, argb: string) => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } } }

  // Title
  const t = ws.getCell(1, 1)
  t.value = `Management P&L — ${data.entity_label}   ·   Season ${data.season}`
  ws.mergeCells(1, 1, 1, ytdCol); t.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } }; fillCell(t, GREEN); t.alignment = { vertical: 'middle' }
  ws.getRow(1).height = 22

  // Header row (row 2)
  const hdr = ws.getRow(2)
  hdr.getCell(1).value = ''
  data.months.forEach((m, i) => { hdr.getCell(firstMonthCol + i).value = `${m.short} '${String(m.cal_year).slice(2)}${m.locked ? ' *' : ''}` })
  hdr.getCell(ytdCol).value = 'YTD'
  hdr.eachCell((c) => { c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; fillCell(c, GREEN); c.alignment = { horizontal: 'right' }; box(c) })
  hdr.getCell(1).alignment = { horizontal: 'left' }

  let row = 3
  // Write one line: label + per-month values + a YTD =SUM formula.
  const writeLine = (label: string, line: PnlLine, opts: { bold?: boolean; indent?: boolean; signed?: boolean; bg?: string }) => {
    const r = ws.getRow(row)
    const lab = r.getCell(1)
    lab.value = (opts.indent ? '    ' : '') + label
    lab.font = { bold: !!opts.bold }
    if (opts.bg) fillCell(lab, opts.bg)
    box(lab)
    const fmt = opts.signed ? SIGNED : MAG
    for (let i = 0; i < nMonths; i++) {
      const c = r.getCell(firstMonthCol + i)
      c.value = Math.round(line.values[i] ?? 0)
      c.numFmt = fmt; c.alignment = { horizontal: 'right' }; if (opts.bold) c.font = { bold: true }; if (opts.bg) fillCell(c, opts.bg); box(c)
    }
    const y = r.getCell(ytdCol)
    y.value = { formula: `SUM(${colLetter(firstMonthCol)}${row}:${colLetter(ytdCol - 1)}${row})` }
    y.numFmt = fmt; y.font = { bold: true }; y.alignment = { horizontal: 'right' }; if (opts.bg) fillCell(y, opts.bg); box(y)
    row++
  }

  for (const cat of data.categories) {
    // subtotal row: sum of its sub rows (written below), one formula per column
    const subtotalRow = row
    // reserve/writing the subtotal after knowing sub rows would need forward refs;
    // simplest is to write subs first into a buffer of row numbers, then the
    // subtotal referencing them. We write subtotal first (with a range formula)
    // and place subs immediately after so the range is contiguous.
    row++ // leave the subtotal row, fill after subs
    const subFirst = row
    for (const s of cat.subs) writeLine(s.sub, s, { indent: true })
    const subLast = row - 1
    // now fill the subtotal row
    const sr = ws.getRow(subtotalRow)
    const lab = sr.getCell(1); lab.value = cat.category; lab.font = { bold: true }; fillCell(lab, SUBTOTAL_BG); box(lab)
    for (let i = 0; i < nMonths; i++) {
      const col = firstMonthCol + i
      const c = sr.getCell(col)
      c.value = subLast >= subFirst ? { formula: `SUM(${colLetter(col)}${subFirst}:${colLetter(col)}${subLast})` } : 0
      c.numFmt = MAG; c.font = { bold: true }; c.alignment = { horizontal: 'right' }; fillCell(c, SUBTOTAL_BG); box(c)
    }
    const yc = sr.getCell(ytdCol)
    yc.value = { formula: `SUM(${colLetter(firstMonthCol)}${subtotalRow}:${colLetter(ytdCol - 1)}${subtotalRow})` }
    yc.numFmt = MAG; yc.font = { bold: true }; yc.alignment = { horizontal: 'right' }; fillCell(yc, SUBTOTAL_BG); box(yc)
  }

  writeLine('Operating result (Profit & Loss)', data.operating, { bold: true, signed: true, bg: RESULT_BG })

  if (data.exceptional.subs.length > 0) {
    // Exceptional subtotal + subs
    const subtotalRow = row; row++
    const subFirst = row
    for (const s of data.exceptional.subs) writeLine(s.sub, s, { indent: true })
    const subLast = row - 1
    const sr = ws.getRow(subtotalRow)
    const lab = sr.getCell(1); lab.value = 'Exceptional Items'; lab.font = { bold: true }; fillCell(lab, SUBTOTAL_BG); box(lab)
    for (let i = 0; i < nMonths; i++) {
      const col = firstMonthCol + i
      const c = sr.getCell(col)
      c.value = subLast >= subFirst ? { formula: `SUM(${colLetter(col)}${subFirst}:${colLetter(col)}${subLast})` } : 0
      c.numFmt = MAG; c.font = { bold: true }; c.alignment = { horizontal: 'right' }; fillCell(c, SUBTOTAL_BG); box(c)
    }
    const yc = sr.getCell(ytdCol)
    yc.value = { formula: `SUM(${colLetter(firstMonthCol)}${subtotalRow}:${colLetter(ytdCol - 1)}${subtotalRow})` }
    yc.numFmt = MAG; yc.font = { bold: true }; yc.alignment = { horizontal: 'right' }; fillCell(yc, SUBTOTAL_BG); box(yc)
  }

  writeLine('Total', data.total, { bold: true, signed: true, bg: RESULT_BG })

  // footnote
  row++
  const note = ws.getCell(row, 1)
  note.value = `Income shown positive; expenses listed as costs. Operating result = Income less Direct, Semi-Direct and Indirect expenses. * = a FINAL (signed-off) month.${data.last_refresh ? `  Data updated ${new Date(data.last_refresh).toLocaleString('en-GB')}.` : ''}`
  ws.mergeCells(row, 1, row, ytdCol); note.font = { italic: true, size: 9, color: { argb: 'FF6B6B6B' } }; note.alignment = { wrapText: true }

  return wb
}

export async function downloadPnlXlsx(data: PnlXlsxData): Promise<void> {
  const wb = await buildPnlWorkbook(data)
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `NYMR-PnL-${data.entity_label}-${data.season.replace(/\//g, '-')}.xlsx`
  document.body.appendChild(a); a.click(); document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 4000)
}
